import { useCallback, useState, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  Button,
  InlineStack,
  Box,
  Icon,
  SkeletonThumbnail,
  InlineGrid,
  DropZone,
  EmptyState,
  Banner,
  Divider,
  Bleed,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  EditIcon,
  PlayIcon,
  DeleteIcon,
  PlusCircleIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { AddMediaModal, MediaEntityType } from "@shared/ui";
import { Instructor, ImageDetailType, MediaItem } from "@features/events/types";
import { getLocalizedText } from "@shared/lib";

// Helper function to get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([^\s&]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

// Helper function to get localized name
// Removed: Using getLocalizedText from utils instead

interface MediaItemProps {
  item: MediaItem;
  onRemove: (itemId: string) => void;
  onEdit: (item: MediaItem) => void;
  language: string;
}

const MediaItemComponent: React.FC<MediaItemProps> = ({
  item,
  onRemove,
  onEdit,
  language,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`Edit ${getLocalizedText(item.name, language) || "media item"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(item.name, language) || "media item"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            {item.type === "vid" ? (
              item.source && getYouTubeThumbnail(item.source as string) ? (
                <img
                  src={getYouTubeThumbnail(item.source as string)!}
                  alt={getLocalizedText(item.name, language)}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Icon source={PlayIcon} />
              )
            ) : item.source ? (
              <img
                srcSet={
                  Array.isArray(item.source)
                    ? item.source
                        .map(({ src, width }) => `${src} ${width}w`)
                        .join(", ")
                    : undefined
                }
                sizes={"48px"}
                src={
                  Array.isArray(item.source) ? item.source[0]?.src : item.source
                }
                alt={getLocalizedText(item.name, language)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <SkeletonThumbnail size="small" />
            )}

            <BlockStack gap="100">
              <Text as="span" variant="bodyMd">
                {getLocalizedText(item.name, language)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200" align="space-between" blockAlign="center">
            <Box></Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={() => onEdit(item)}
                />
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() =>
                    onRemove(getLocalizedText(item.name, language))
                  }
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

interface ProfileImageAndMediaCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
  fieldErrors: Record<string, string | undefined>;
}

export default function ProfileImageAndMediaCard({
  instructorData,
  onDataChange,
  fieldErrors,
}: ProfileImageAndMediaCardProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { POST } = useApiRequest();

  // Get media related errors
  const mediaErrors = useMemo(() => {
    const errors: string[] = [];
    Object.keys(fieldErrors).forEach((key) => {
      if (key.startsWith("media.")) {
        const error = fieldErrors[key];
        if (error) {
          errors.push(error);
        }
      }
    });
    return errors;
  }, [fieldErrors]);

  // Profile image upload mutation
  const { mutateAsync: uploadProfileImage, isPending: isUploadingProfile } =
    useMutation({
      mutationFn: async (
        file: File
      ): Promise<ImageDetailType[] | ImageDetailType> => {
        const formData = new FormData();
        formData.append("file", file);

        if (!hcc || !instructorData.id) {
          throw new Error(
            "HCC and Instructor ID are required for profile image upload"
          );
        }

        return POST(
          `/api/healthcare-companies/${hcc}/e/instructors/${instructorData.id}/avatar`,
          formData
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["instructor", hcc, instructorData.id],
        });
        queryClient.invalidateQueries({ queryKey: ["event-instructors"] });
      },
    });

  // Extract current values from instructorData
  const profileImage = instructorData.avatar?.[0];
  const mediaItems = instructorData.media || [];

  const handleProfileImageUpload = useCallback(
    async (files: File[]) => {
      if (files.length > 0 && instructorData.id) {
        try {
          const response = await uploadProfileImage(files[0]);
          const normalizedAvatar = Array.isArray(response)
            ? response
            : response
              ? [response]
              : [];

          // Update avatar with a flat ImageDetailType[] shape.
          onDataChange({
            avatar: normalizedAvatar.length > 0 ? normalizedAvatar : undefined,
          });
        } catch (error) {
          console.error("Profile image upload failed:", error);
        }
      }
    },
    [uploadProfileImage, instructorData.id, onDataChange]
  );

  const handleRemoveProfileImage = useCallback(() => {
    onDataChange({ avatar: undefined });
  }, [onDataChange]);

  const renderMediaItem = (
    item: MediaItem
  ) => (
    <MediaItemComponent
      item={item}
      onRemove={handleRemoveMedia}
      onEdit={handleEditClick}
      language={i18n.language}
    />
  );

  const handleAddMedia = (mediaArray: MediaItem[]) => {
    // Add all new media items
    onDataChange({ media: [...mediaItems, ...mediaArray] });
  };

  const handleEditMedia = (updatedMedia: MediaItem) => {
    if (editingIndex === null) return;

    // Replace the item at the specific index with the updated media
    const updatedItems = [...mediaItems];
    updatedItems[editingIndex] = updatedMedia;
    onDataChange({ media: updatedItems });
  };

  const handleRemoveMedia = (id: string) => {
    const updatedItems = mediaItems.filter(
      (item) => getLocalizedText(item.name) !== id
    );
    onDataChange({ media: updatedItems });
  };

  const handleEditClick = (item: MediaItem) => {
    // Find the index of the item being edited
    const itemIndex = mediaItems.findIndex((mediaItem) => mediaItem.name.bg === item.name.bg);

    // Pass the item directly without unnecessary conversion
    // This preserves the exact data format expected by AddMediaModal
    setEditingItem(item);
    setEditingIndex(itemIndex);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("instructorDetails.profileImageAndMedia.title")}
        </Text>

        {/* Profile Image Section */}
        <BlockStack gap="200">
          {!profileImage && (
            <Banner tone="warning">
              <Text as="p" variant="bodyMd">
                {t("instructorDetails.profileImageAndMedia.noProfileImageWarning")}
              </Text>
            </Banner>
          )}

          <Text as="h3" variant="bodyMd" requiredIndicator>
            {t("instructorDetails.profileImageAndMedia.profileImage")}
          </Text>


          {profileImage ? (
            <Box padding="400" borderRadius="200" background="bg-surface">
              <BlockStack align="center" gap="200" inlineAlign="center">
                <img
                  src={profileImage.src}
                  alt={t("instructorDetails.profileImageAndMedia.profileImageAlt")}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "20px",
                    objectFit: "cover",
                  }}
                />
                <Button
                  variant="tertiary"
                  icon={DeleteIcon}
                  tone="critical"
                  onClick={handleRemoveProfileImage}
                  disabled={isUploadingProfile}
                  size="micro"
                >
                  {t(
                    "instructorDetails.profileImageAndMedia.removeProfileImage"
                  )}
                </Button>
              </BlockStack>
            </Box>
          ) : (
            <DropZone
              onDrop={handleProfileImageUpload}
              accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
              disabled={isUploadingProfile}
            >
              <DropZone.FileUpload
                actionTitle={t(
                  "instructorDetails.profileImageAndMedia.uploadProfileImage"
                )}
                actionHint={t(
                  "instructorDetails.profileImageAndMedia.uploadHint"
                )}
              />
            </DropZone>
          )}
        </BlockStack>

        <Bleed marginInline="400">
          <Divider />
        </Bleed>  


        {/* Media Section */}
        <BlockStack gap="200">
          <Text as="h3" variant="bodyMd">
            {t("instructorDetails.profileImageAndMedia.videosAndImages")}
          </Text>

          {mediaErrors.length > 0 && (
            <Banner tone="critical">
              <BlockStack gap="100">
                {mediaErrors.map((error, index) => (
                  <Text key={index} as="p" variant="bodyMd">
                    {error}
                  </Text>
                ))}
              </BlockStack>
            </Banner>
          )}

          {mediaItems.length === 0 ? (
            <EmptyState
              heading={t("instructorDetails.profileImageAndMedia.noMediaAdded")}
              action={{
                content: t("instructorDetails.profileImageAndMedia.addMedia"),
                icon: PlusIcon,
                onAction: () => setIsModalOpen(true),
              }}
              image=""
            />
          ) : (
            <Box
              borderWidth="025"
              borderColor="border"
              borderRadius="200"
              background="bg-surface"
            >
              <SortableList
                items={mediaItems}
                onChange={(reorderedMedia) => {
                  onDataChange({ media: reorderedMedia });
                }}
                idForItem={(item) => getLocalizedText(item.name)}
                renderItem={(item) => renderMediaItem(item)}
                showDividers={true}
              />
            </Box>
          )}

          {mediaItems.length > 0 && (
            <Box>
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="plain"
                icon={PlusCircleIcon}
              >
                {t("instructorDetails.profileImageAndMedia.addMedia")}
              </Button>
            </Box>
          )}
        </BlockStack>
      </BlockStack>

      <AddMediaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddMedia={handleAddMedia}
        onEditMedia={handleEditMedia}
        editingItem={editingItem}
        hcc={hcc}
        entityType={MediaEntityType.INSTRUCTOR}
        entityId={instructorData.id}
        instructorData={instructorData}
      />
    </Card>
  );
}
